import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Table } from '~/components/molecules/Table';
import {
  UserBadgeListItem,
  UserBadgeListItemSkeleton,
} from '~/components/organisms/UserBadgeListItem';
import { useUserBadgesQuery } from '~/lib/api/modules/UserBadges/hooks/useUserBadgesQuery';

const GRID_TEMPLATE_COLUMNS = '1fr repeat(3, min-content)';
const DEFAULT_SKELETON_COUNT = 3;

export type IUserBadgeListProps = IContainerProps & {
  appId: string;
};

export const UserBadgeList: React.FC<IUserBadgeListProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const userBadgesQuery = useUserBadgesQuery({ appId });

  const userBadges = userBadgesQuery.data?.userBadges;
  const isEmpty = !userBadgesQuery.isLoading && !userBadges?.length;

  return (
    <ContentBlock sx={sx}>
      {userBadgesQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={userBadgesQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('UserBadgeList.empty.title')}>
          <Button href={`/apps/${appId}/badges/create`}>
            {t('UserBadgeList.empty.action-label')}
          </Button>
        </ContentBlock.Empty>
      ) : (
        <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          <Table.Header>{t('common.name')}</Table.Header>

          {userBadgesQuery.isLoading
            ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                <UserBadgeListItemSkeleton key={index} />
              ))
            : userBadges?.map((userBadge) => (
                <UserBadgeListItem key={userBadge._id} userBadge={userBadge} />
              ))}
        </Table>
      )}
    </ContentBlock>
  );
};
